import { ReactNode } from "react";
import { MergeIcon, PencilIcon, TrashIcon, Undo2Icon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/Base/Tooltip";
import { DatatableEmployee } from "@/components/Employees/Employees.columns";
import { isManualEmployee } from "@/lib/employees";

interface EmployeeRowActionsProps {
  employee: DatatableEmployee;
  onEdit: (employee: DatatableEmployee) => void;
  onDelete: (employee: DatatableEmployee) => void;
  onRestore: (employee: DatatableEmployee) => void;
  /** Super admins only: when omitted, no Merge button is shown. */
  onMerge?: (employee: DatatableEmployee) => void;
}

/**
 * The icon buttons in an employee row's actions column. A deleted employee
 * can only be restored or, for a manual one, merged into a synced employee;
 * every other action would act on a record the rest of the app treats as gone.
 */
export const employeeRowActions = ({
  employee,
  onEdit,
  onDelete,
  onRestore,
  onMerge,
}: EmployeeRowActionsProps): ReactNode[] => {
  const mergeAction =
    onMerge && isManualEmployee(employee)
      ? [
          <TooltipProvider key={"merge"}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => onMerge(employee)}
                  variant="ghost"
                  id="merge_employee"
                  aria-label="Merge into a synced team member"
                >
                  <MergeIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Merge this manually created team member into a synced one.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>,
        ]
      : [];
  if (employee._deleted) {
    return [
      <TooltipProvider key={"restore"}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => onRestore(employee)}
              variant="ghost"
              id="restore_employee"
              aria-label="Restore team member"
            >
              <Undo2Icon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Restore this deleted team member.</TooltipContent>
        </Tooltip>
      </TooltipProvider>,
      ...mergeAction,
    ];
  }
  return [
    <TooltipProvider key={"update"}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={() => onEdit(employee)}
            variant="ghost"
            id="edit_employee"
          >
            <PencilIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Update this team member&#39;s details.</TooltipContent>
      </Tooltip>
    </TooltipProvider>,
    <TooltipProvider key={"delete"}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={() => onDelete(employee)}
            variant="ghost"
            id="delete_employee"
          >
            <TrashIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          Delete this team member. Only a super admin can restore them.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>,
    ...mergeAction,
  ];
};
